/**
 * Main record button — Pro Tools-style transport REC button.
 *
 * Single circular bevel-button with state machine driving label + variant +
 * pulse. Uses `.pt-rec-btn` for the skeuomorphic chrome (radial gradient,
 * inset highlights, halo glow when armed) — see recorder.css.
 *
 * States (from useRecordingStore.phase):
 *   idle/preload/review/advance → red "Record (Enter)"
 *   three_pop                   → amber "Get ready..."
 *   recording                   → red pulsing halo "Recording — Esc to stop"
 *   qc_local/upload/qc_server   → grey spinner "Processing..."
 */
import { cx } from './cx'
import type { Phase } from './state'

export interface BigRecordButtonProps {
  phase: Phase
  /** Disabled (e.g. no cue selected, mic permission denied, recorder loading). */
  disabled?: boolean
  /**
   * If non-null, the button shows this text instead of the phase label and
   * is treated as a loading state. Used to surface "Loading recorder…" while
   * the AudioWorklet primes on first mount.
   */
  loadingLabel?: string | null
  /** Click triggers begin/stop based on phase. */
  onClick: () => void
  /** Locale-driven label. Default 'en'. */
  locale?: 'zh' | 'en'
}

export function BigRecordButton({
  phase,
  disabled,
  loadingLabel,
  onClick,
  locale = 'en',
}: BigRecordButtonProps) {
  const { label, hint, kbd, tone, ariaLabel } = labelFor(phase, locale)
  const isLoading = !!loadingLabel
  const effectiveLabel = isLoading ? 'WAIT' : label
  const effectiveHint = isLoading ? loadingLabel : hint
  const effectiveAriaLabel = isLoading ? loadingLabel : ariaLabel
  const effectiveTone: Tone = isLoading ? 'processing' : tone

  const variantClass =
    effectiveTone === 'arming'
      ? 'pt-rec-btn--ready'
      : effectiveTone === 'recording'
        ? 'pt-rec-btn--armed'
        : effectiveTone === 'processing'
          ? 'pt-rec-btn--processing'
          : effectiveTone === 'review'
            ? 'pt-rec-btn--review'
            : ''

  return (
    <div className="flex flex-col items-center justify-center gap-2">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={effectiveAriaLabel}
        aria-pressed={phase === 'recording'}
        aria-busy={isLoading}
        data-phase={phase}
        data-tour="record-button"
        className={cx('pt-rec-btn', variantClass, 'focus-visible:outline-none')}
      >
        <span className="block leading-tight">{effectiveLabel}</span>
      </button>
      {/* Hint row — small SF Mono caption below the button (PT-style label). */}
      <div className="flex items-center gap-1.5 pt-mono text-[10px] uppercase tracking-[0.14em] text-pt-text-soft">
        <span>{effectiveHint}</span>
        {kbd && !isLoading ? <span className="pt-kbd">{kbd}</span> : null}
      </div>
    </div>
  )
}

type Tone = 'idle' | 'arming' | 'recording' | 'processing' | 'review'

interface LabelInfo {
  label: string
  hint: string
  /** Optional keyboard hint chip (Enter / Esc / N). */
  kbd: string | null
  tone: Tone
  ariaLabel: string
}

function labelFor(phase: Phase, locale: 'zh' | 'en'): LabelInfo {
  switch (phase) {
    case 'three_pop':
      return locale === 'zh'
        ? { label: '准备', hint: '3-pop', kbd: null, tone: 'arming', ariaLabel: '准备录音' }
        : {
            label: 'Ready',
            hint: 'count-in',
            kbd: null,
            tone: 'arming',
            ariaLabel: 'Three-pop count-in',
          }
    case 'recording':
      return locale === 'zh'
        ? {
            label: '● REC',
            hint: '取消',
            kbd: 'Esc',
            tone: 'recording',
            ariaLabel: '录音中,按 Esc 取消',
          }
        : {
            label: '● REC',
            hint: 'abort',
            kbd: 'Esc',
            tone: 'recording',
            ariaLabel: 'Recording, press Esc to abort',
          }
    case 'qc_local':
    case 'upload':
    case 'qc_server':
      return locale === 'zh'
        ? {
            label: '处理中',
            hint: 'QC + 上传',
            kbd: null,
            tone: 'processing',
            ariaLabel: '处理本条 take 中',
          }
        : {
            label: 'Wait',
            hint: 'qc · upload',
            kbd: null,
            tone: 'processing',
            ariaLabel: 'Processing take',
          }
    case 'review':
      return locale === 'zh'
        ? {
            label: '审听',
            hint: '下一条',
            kbd: 'N',
            tone: 'review',
            ariaLabel: '审听 take,按 N 下一条或 R 重录',
          }
        : {
            label: 'Review',
            hint: 'next',
            kbd: 'N',
            tone: 'review',
            ariaLabel: 'Review take, press N for next or R for retake',
          }
    default:
      return locale === 'zh'
        ? { label: 'REC', hint: '开始', kbd: '↵', tone: 'idle', ariaLabel: '开始录音' }
        : { label: 'REC', hint: 'start', kbd: '↵', tone: 'idle', ariaLabel: 'Start recording' }
  }
}
